import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toTotal } from "@/lib/plannerPoints";
import {
  MANUAL_ASSIGNEE,
  PRIORITIES,
  SERVICE_BUCKETS,
  type PlannerTask,
  type CatalogItem,
} from "@/services/plannerService";

/**
 * Parity pass 2: the webapp's Add/Edit Task modal, field for field.
 *
 * Faithful details that matter: the catalog picker autofills points AND
 * bucket; qty accepts quarter steps (0.25); the duplicate guard warns once
 * and relabels the submit to "Add Anyway" rather than blocking; notes and
 * internal notes are separate fields (client-visible vs internal).
 *
 * Points are the ROW TOTAL here, as in the webapp (Q1): a catalog pick fills
 * rate × qty and follows qty until the user types a total. The page converts
 * to per unit on submit (O3); this dialog never does.
 */

export interface TaskDraft {
  billing_period: string;
  task_type: string;
  service_bucket: string;
  /** Row total (not per unit). */
  points: number;
  qty: number;
  status: "not_started" | "done";
  priority: string;
  assignee: string;
  due_date: string;
  notes: string;
  internal_notes: string;
  is_recurring: boolean;
}

const FIELD = "h-9 text-sm";
const NO_LABELS: Record<string, string> = {};
const NO_NAMES: string[] = [];
/** Radix Select can't take "" as an item value; this one saves "". */
const UNASSIGNED = "__unassigned";
const LABEL = "mb-1 block text-xs font-medium text-muted-foreground";

function emptyDraft(period: string): TaskDraft {
  return {
    billing_period: period,
    task_type: "",
    service_bucket: "",
    points: 0,
    qty: 1,
    status: "not_started",
    priority: "none",
    assignee: "",
    due_date: "",
    notes: "",
    internal_notes: "",
    is_recurring: false,
  };
}

export function TaskDialog({
  open,
  onOpenChange,
  period,
  periods,
  catalog,
  existingTypes,
  assignees,
  assigneeLabels = NO_LABELS,
  inactiveAssignees = NO_NAMES,
  task,
  saving,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  period: string;
  periods: string[];
  catalog: CatalogItem[];
  /** Task types already present in the target period — drives the guard. */
  existingTypes: string[];
  /** Assignee choices (MISC + Teamwork staff + names in use). The dialog adds
   *  Unassigned and "+ Custom…", keeps the task's current assignee, and never
   *  offers "(manual)". */
  assignees: string[];
  /** name -> display name (an admin's override, O31); the name is saved. */
  assigneeLabels?: Record<string, string>;
  /** People an admin marked inactive: never autofilled from a catalog default (O38). */
  inactiveAssignees?: string[];
  /** null = add; a task = edit. */
  task: PlannerTask | null;
  saving: boolean;
  onSubmit: (draft: TaskDraft) => void;
}) {
  const editing = task != null;
  const [draft, setDraft] = useState<TaskDraft>(() => emptyDraft(period));
  const [typeSearch, setTypeSearch] = useState("");
  const [typeOpen, setTypeOpen] = useState(false);
  const [dupAcknowledged, setDupAcknowledged] = useState(false);
  const [customAssignee, setCustomAssignee] = useState(false);
  const [error, setError] = useState("");
  // The picked catalog rate while Points still follows qty; null once the
  // user types a total (or when editing, where the stored total stands).
  const [trackedRate, setTrackedRate] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    setDupAcknowledged(false);
    setTypeSearch("");
    setTrackedRate(null);
    setDraft(
      task
        ? {
            billing_period: task.billing_period,
            task_type: task.task_type,
            service_bucket: task.service_bucket ?? "",
            points: toTotal(task.points, task.qty),
            qty: task.qty ?? 1,
            status: task.status === "done" ? "done" : "not_started",
            priority: task.priority || "none",
            assignee: task.assignee ?? "",
            due_date: task.due_date ?? "",
            notes: task.notes ?? "",
            internal_notes: task.internal_notes ?? "",
            is_recurring: Boolean(task.is_recurring),
          }
        : emptyDraft(period),
    );
  }, [open, task, period]);

  const set = (patch: Partial<TaskDraft>) => setDraft((d) => ({ ...d, ...patch }));

  // Upstream opens the catalog on FOCUS and narrows as you type — an empty
  // box must still show the list, or the field reads as "no data".
  const matches = (
    typeSearch.trim()
      ? catalog.filter((c) =>
          c.name.toLowerCase().includes(typeSearch.trim().toLowerCase()),
        )
      : catalog
  ).slice(0, 20);

  const pickType = (c: CatalogItem) => {
    // Upstream writes task_type, service_bucket, points, ASSIGNEE and NOTES
    // in one update — picking "Google Ad Spend" should land its owner and
    // its boilerplate, not just the points.
    const rate = c.points_per_unit ?? 0;
    setTrackedRate(rate);
    set({
      task_type: c.name,
      points: toTotal(rate, draft.qty),
      service_bucket: c.service_bucket || draft.service_bucket,
      // Never clobber something the user already typed.
      assignee:
        draft.assignee ||
        (c.default_assignee === MANUAL_ASSIGNEE || inactiveAssignees.includes(c.default_assignee ?? "")
          ? ""
          : c.default_assignee) ||
        "",
      notes: draft.notes || c.default_notes || "",
    });
    setTypeSearch("");
  };

  // The task's current assignee stays selectable even when the list lacks it
  // (a legacy name), so saving never silently drops it (O32).
  const listed = assignees.filter((a) => a && a !== MANUAL_ASSIGNEE);
  const current = draft.assignee;
  const assigneeChoices =
    current && current !== MANUAL_ASSIGNEE && !listed.includes(current)
      ? [...listed, current]
      : listed;

  const duplicate =
    !editing &&
    draft.task_type.trim() !== "" &&
    existingTypes.some((t) => t.toLowerCase() === draft.task_type.trim().toLowerCase());

  const submit = () => {
    if (!draft.task_type.trim()) {
      setError("Task type is required.");
      return;
    }
    if (!draft.billing_period) {
      setError("Billing period is required.");
      return;
    }
    if (!(draft.qty > 0)) {
      setError("Qty must be greater than 0.");
      return;
    }
    if (duplicate && !dupAcknowledged) {
      setDupAcknowledged(true);
      return;
    }
    onSubmit(draft);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit Task: ${task?.task_type}` : "Add Task"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className={LABEL}>Billing Period *</Label>
              <Select value={draft.billing_period} onValueChange={(v) => set({ billing_period: v })}>
                <SelectTrigger className={FIELD}>
                  <SelectValue placeholder="— select period —" />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="relative">
              <Label className={LABEL}>Task Type *</Label>
              <Input
                className={FIELD}
                value={typeSearch || draft.task_type}
                placeholder="Search task type…"
                onFocus={() => setTypeOpen(true)}
                onBlur={() => window.setTimeout(() => setTypeOpen(false), 150)}
                onChange={(e) => {
                  setTypeSearch(e.target.value);
                  set({ task_type: e.target.value });
                  setTypeOpen(true);
                }}
              />
              {typeOpen && matches.length > 0 ? (
                <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-xl">
                  {matches.map((c) => (
                    <li
                      key={c.id}
                      role="option"
                      aria-selected={false}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickType(c);
                        setTypeOpen(false);
                      }}
                      className="cursor-pointer border-b border-border px-3 py-2 text-sm last:border-0 hover:bg-accent"
                    >
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.points_per_unit} pt per unit · {c.service_bucket || "—"}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={LABEL}>Qty</Label>
              <Input
                type="number"
                step="0.25"
                min={0}
                className={FIELD}
                value={draft.qty}
                onChange={(e) => {
                  const qty = Number(e.target.value);
                  set(trackedRate === null ? { qty } : { qty, points: toTotal(trackedRate, qty) });
                }}
              />
            </div>
            <div>
              <Label className={LABEL}>Points (total)</Label>
              <Input
                type="number"
                step="0.25"
                min={0}
                className={FIELD}
                value={draft.points}
                onChange={(e) => {
                  setTrackedRate(null); // a typed total stops following qty
                  set({ points: Number(e.target.value) });
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={LABEL}>Service Bucket</Label>
              <Select value={draft.service_bucket || undefined} onValueChange={(v) => set({ service_bucket: v })}>
                <SelectTrigger className={FIELD}>
                  <SelectValue placeholder="— select bucket —" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_BUCKETS.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={LABEL}>Assignee</Label>
              {customAssignee ? (
                <div className="flex gap-1">
                  <Input
                    className={FIELD}
                    autoFocus
                    value={draft.assignee}
                    placeholder="Type a name"
                    onChange={(e) => set({ assignee: e.target.value })}
                  />
                  <Button variant="outline" size="sm" className="h-9" onClick={() => setCustomAssignee(false)}>
                    List
                  </Button>
                </div>
              ) : (
                <Select
                  // "" and a stored "(manual)" both show the Unassigned
                  // placeholder. Neither is the Unassigned item's value, so
                  // picking Unassigned is a real change that saves "", while an
                  // untouched "(manual)" is saved as stored (O33).
                  value={draft.assignee === MANUAL_ASSIGNEE ? "" : draft.assignee}
                  onValueChange={(v) => {
                    if (v === "__custom") {
                      setCustomAssignee(true);
                      set({ assignee: "" });
                    } else {
                      set({ assignee: v === UNASSIGNED ? "" : v });
                    }
                  }}
                >
                  <SelectTrigger className={FIELD}>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                    {assigneeChoices.map((a) => (
                      <SelectItem key={a} value={a}>{assigneeLabels[a] ?? a}</SelectItem>
                    ))}
                    <SelectItem value="__custom">+ Custom…</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <Label className={LABEL}>Due Date</Label>
              <Input
                type="date"
                className={FIELD}
                value={draft.due_date}
                onChange={(e) => set({ due_date: e.target.value })}
              />
            </div>
            <div>
              <Label className={LABEL}>Status</Label>
              <Select value={draft.status} onValueChange={(v) => set({ status: v as TaskDraft["status"] })}>
                <SelectTrigger className={FIELD}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Pending</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={LABEL}>Priority</Label>
              <Select value={draft.priority} onValueChange={(v) => set({ priority: v })}>
                <SelectTrigger className={FIELD}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p === "none" ? "None" : p[0].toUpperCase() + p.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={draft.is_recurring}
              onChange={(e) => set({ is_recurring: e.target.checked })}
            />
            Repeats
            {!draft.is_recurring ? (
              <span className="text-xs text-muted-foreground">
                — offer this task for one-click copy into a future month
              </span>
            ) : null}
          </label>

          <div>
            <Label className={LABEL}>Notes</Label>
            <Textarea
              rows={2}
              value={draft.notes}
              placeholder="Client-visible context"
              onChange={(e) => set({ notes: e.target.value })}
            />
          </div>
          <div>
            <Label className={LABEL}>Internal Notes</Label>
            <Textarea
              rows={2}
              value={draft.internal_notes}
              placeholder="Internal — hidden from clients"
              onChange={(e) => set({ internal_notes: e.target.value })}
            />
          </div>

          {duplicate && dupAcknowledged ? (
            <div className="flex items-start gap-2 rounded-md border border-amber/40 bg-amber-light p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-dark" />
              <span>
                A <strong>{draft.task_type}</strong> task already exists for {draft.billing_period}.
                Click <strong>Add Anyway</strong> to add another.
              </span>
            </div>
          ) : null}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={saving || !draft.task_type.trim()}>
            {saving
              ? "Saving…"
              : editing
                ? "Save Changes"
                : duplicate && dupAcknowledged
                  ? "Add Anyway"
                  : "Add Task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TaskDialog;
